import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {getHighImpactChangelogFromChangelog} from 'config-migration/import/HighImpactChanges';
import {
  CHANGELOG_HIDDEN_FIELD_NAMES,
  CHANGELOG_VERB_PAST_TENSE,
  ChangelogVerb,
  DIFF_TYPE_TO_VERB_PRESENT,
  DIFF_TYPE_TO_VERB_PAST,
  MigrationChangelogRenderType,
  MigrationChangeType,
  MigrationDiff,
  MigrationObjectChange,
  PathSegment,
  SegmentType,
  CHANGELOG_VERB_PRESENT_TENSE,
  CONSTRAINT_VERB_TO_DISPLAY_TEXT,
} from 'config-migration/import/import-changelog/MigrationChangelogConstants';
import {generateSections, ObjectSectionConfig} from 'config-migration/import/ObjectSectionConfig';
import {LogLevel} from 'config-migration/migration-log/LogLevel';
import LogSection, {
  getSectionNameForObjectType,
  LogMessageGroup,
} from 'config-migration/migration-log/LogSection';
import {MigrationError} from 'config-migration/MigrationConstants';
import {PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT, PrimaryObjectType} from 'config-migration/types/ObjectType';
import getTestProps from 'test/lib/cypress/getTestProps';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {LogDetail, LogMessageData, makeSimpleLogMessage} from '../../migration-log/Log';
import {getHighImpactChangeFromMigrationDiff} from '../HighImpactChanges';

export enum ChangelogSource {
  REVIEW = 'review',
  SUMMARY = 'summary',
}

export enum ChangelogType {
  CONSTRAINT = 'constraint',
}

export interface ImportChangelogProps {
  changelogSource: ChangelogSource;
  changelog: Array<MigrationObjectChange>;
  errorsByObjectId: {[objectId: string]: Array<MigrationError>};
  showObjectUrl: boolean;
}

export interface ImportChangelogSectionProps {
  changelogSource: ChangelogSource;
  /** Nested to support log groups in a changelog, ie. Templates grouped by their TemplateCollection */
  changes: Array<Array<MigrationObjectChange>>;
  errorsByObjectId: {[objectId: string]: Array<MigrationError>};
  sectionDisplayName: string;
  showObjectUrl: boolean;
}

interface ImportDiffDescriptionProps {
  diff: MigrationDiff;
  changelogSource: ChangelogSource;
  renderType: MigrationChangelogRenderType;
  parentObjectDisplayName: string;
  parentObjectType: PrimaryObjectType;
}

function applyStylingToDiffPath(
  diffPath: Array<PathSegment>,
  parentObjectDisplayName: string
): Array<string | JSX.Element> {
  // We fall back to the parent name if path is null
  if (!diffPath.length) {
    return [<strong key={0}>{parentObjectDisplayName}</strong>];
  }

  // First we apply bold styling to the labeled path segments
  const boldedPathSegments = diffPath.map((segment, index) =>
    segment.segmentType !== SegmentType.FIELD_LABEL ? (
      <strong key={index}>{segment.segmentName}</strong>
    ) : (
      segment.segmentName
    )
  );

  // Next we join the path segments with space
  const spaceSeparatedPathSegments = boldedPathSegments.flatMap((segment, index, path) =>
    path.length - 1 !== index ? [segment, ' '] : segment
  );

  return spaceSeparatedPathSegments;
}

export function ImportDiffDescription(props: ImportDiffDescriptionProps): JSX.Element {
  const {diff, changelogSource, renderType, parentObjectDisplayName, parentObjectType} = props;
  // Use future tense for review page since we're showing the changes that will be made
  const futureTense = changelogSource === ChangelogSource.REVIEW;
  const verb = futureTense
    ? CHANGELOG_VERB_PRESENT_TENSE[diff.changelogVerb]
    : CHANGELOG_VERB_PAST_TENSE[diff.changelogVerb];
  const pathSegments = diff.diffPath.slice(0, -1);
  const path = applyStylingToDiffPath(pathSegments, parentObjectDisplayName);
  const fieldName = diff.diffPath.length ? diff.diffPath[diff.diffPath.length - 1].segmentName : '';
  if (diff.diffType === MigrationChangeType.UPDATE) {
    if (CHANGELOG_HIDDEN_FIELD_NAMES.has(fieldName)) {
      // If path is just "identifier", render the object type as well for additional context
      // For example, "Updating identifier" becomes "Updating entity schema identifier"
      const pathIsIdentifier = pathSegments.length === 1 && pathSegments[0].segmentName === 'identifier';
      if (pathIsIdentifier) {
        const objectTypeDisplayText = PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT[parentObjectType].toLowerCase();
        return (
          <span>
            {verb} {objectTypeDisplayText} {path}
          </span>
        );
      }
      return (
        <span>
          {verb} {path}
        </span>
      );
    }
    if (diff.changelogVerb === ChangelogVerb.ARCHIVE || diff.changelogVerb === ChangelogVerb.UNARCHIVE) {
      return (
        <span>
          {verb} {path}
        </span>
      );
    }
    if (diff.changelogVerb === ChangelogVerb.RENAME) {
      return (
        <span>
          {verb} {path} to <strong>{diff.rightValue}</strong>
        </span>
      );
    }
    if (diff.changelogVerb === ChangelogVerb.MARK) {
      return (
        <span>
          {verb} {path}: {fieldName} as <strong>{diff.rightValue}</strong>
        </span>
      );
    }
    if (renderType === MigrationChangelogRenderType.MASK_DETAILS) {
      return (
        <span>
          {verb} {path}: {fieldName} changed
        </span>
      );
    } else {
      return (
        <span>
          {verb} {path}: {fieldName} changed from <strong>{diff.leftValue || 'None'}</strong> to{' '}
          <strong>{diff.rightValue || 'None'}</strong>
        </span>
      );
    }
  } else {
    const className = diff.objectClass;

    if (fieldName === ChangelogType.CONSTRAINT) {
      const displayName = diff.objectDisplayName?.replace(/[()]/g, '');

      return renderConstraintDiff(diff, displayName);
    }

    return (
      <span>
        {verb} {className} {diff.objectDisplayName && <strong>{diff.objectDisplayName}</strong>}
      </span>
    );
  }
}

function renderConstraintDiff(diff: MigrationDiff, displayName: string | undefined): JSX.Element {
  const action = CONSTRAINT_VERB_TO_DISPLAY_TEXT[diff.diffType];

  return (
    <span>
      Constraint {action}
      {displayName && (
        <>
          : <strong>{displayName}</strong>
        </>
      )}
    </span>
  );
}

export function convertChangeToLogMessage(
  change: MigrationObjectChange,
  changelogSource: ChangelogSource,
  errors: Array<MigrationError>,
  url?: string | null
): LogMessageData {
  // Use future tense for review page since we're showing the changes that will be made
  const futureTense = changelogSource === ChangelogSource.REVIEW;
  const verb = futureTense
    ? DIFF_TYPE_TO_VERB_PRESENT[change.objectAction]
    : DIFF_TYPE_TO_VERB_PAST[change.objectAction];

  const diffDetails: Array<LogDetail> = change.diffDetails.map((difference) => {
    const detail: LogDetail = {
      message: (
        <ImportDiffDescription
          diff={difference}
          changelogSource={changelogSource}
          renderType={difference.renderType}
          parentObjectDisplayName={change.objectDisplayName}
          parentObjectType={change.objectType}
        />
      ),
      level: LogLevel.INFO,
    };
    const highImpactChange = getHighImpactChangeFromMigrationDiff(difference, change);
    if (highImpactChange) {
      detail.highImpactChange = highImpactChange;
    }
    return detail;
  });

  const errorDetails: Array<LogDetail> = errors.map((error) => ({
    message: error.message,
    level: error.level,
  }));
  const displayNameElement = <strong>{change.objectDisplayName}</strong>;

  return {
    message: (
      <span>
        {verb} {PRIMARY_OBJECT_TYPE_TO_DISPLAY_TEXT[change.objectType].toLowerCase()}{' '}
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {displayNameElement}
          </a>
        ) : (
          displayNameElement
        )}
      </span>
    ),
    details: diffDetails.concat(errorDetails),
    changelogSource,
  };
}

/** Filter out MigrationObjectChanges that don't include a change or their object has an error */
export function filterChanges(
  changes: Array<MigrationObjectChange>,
  errorsByObjectId: {[objectId: string]: Array<MigrationError>}
): Array<MigrationObjectChange> {
  return changes.filter(
    (objectDiff) =>
      objectDiff.objectAction !== MigrationChangeType.NO_CHANGE || errorsByObjectId[objectDiff.objectId]
  );
}

export function ImportChangelogSection(props: ImportChangelogSectionProps): JSX.Element {
  const logMessageGroups: Array<LogMessageGroup> = [];
  props.changes.forEach((changeGroup) => {
    const filteredChanges = filterChanges(changeGroup, props.errorsByObjectId);
    const logMessages = filteredChanges.map((change) =>
      convertChangeToLogMessage(change, props.changelogSource, props.errorsByObjectId[change.objectId] || [])
    );
    if (logMessages.length === 0) {
      logMessages.push(makeSimpleLogMessage(`No changes to ${props.sectionDisplayName.toLowerCase()}`));
    }
    logMessageGroups.push(logMessages);
  });
  return <LogSection sectionDisplayName={props.sectionDisplayName} logMessageGroups={logMessageGroups} />;
}

function getChangelogSectionKey(change: MigrationObjectChange): string {
  // For grouper objects, special case the changelog section, otherwise return the section for the object type
  if (change.objectType === PrimaryObjectType.Template) {
    if (change.objectAttributes.hasOwnProperty('processTaskSchemaId')) {
      return getSectionNameForObjectType(PrimaryObjectType.ProcessTaskSchema);
    } else {
      return getSectionNameForObjectType(PrimaryObjectType.Template);
    }
  }
  if (
    (change.objectType === PrimaryObjectType.ProcessFlowchartConfigVersion ||
      change.objectType === PrimaryObjectType.ProcessTaskSchema) &&
    change.objectAttributes.hasOwnProperty('parentIdentifier')
  ) {
    return getSectionNameForObjectType(PrimaryObjectType.Template);
  }
  return getSectionNameForObjectType(change.objectType);
}

function updateChangelogGroupsForSection(
  section: {
    objects: Array<MigrationObjectChange>;
    config: ObjectSectionConfig;
  },
  sectionToChangelogGroups: Map<string, Array<Array<MigrationObjectChange>>>,
  sectionConfigMap: Map<string, ObjectSectionConfig>
): void {
  const sectionKey: string = getChangelogSectionKey(section.objects[0]);
  const existingGroup: Array<Array<MigrationObjectChange>> = sectionToChangelogGroups.get(sectionKey) || [];
  if (existingGroup.length === 0) {
    sectionConfigMap.set(sectionKey, section.config);
  }
  existingGroup.push(section.objects);
  sectionToChangelogGroups.set(sectionKey, existingGroup);
}

function getChangelogComponents(
  sectionToChangelogGroups: Map<string, Array<Array<MigrationObjectChange>>>,
  sectionConfigMap: Map<string, ObjectSectionConfig>,
  props: ImportChangelogProps
): Array<JSX.Element> {
  const changelogGroups: Array<JSX.Element> = [];
  sectionToChangelogGroups.forEach((groups, groupKey) => {
    const config = sectionConfigMap.get(groupKey);
    if (config) {
      changelogGroups.push(
        <config.changelogComponent
          key={groupKey}
          changelogSource={props.changelogSource}
          changes={groups}
          errorsByObjectId={props.errorsByObjectId}
          sectionDisplayName={config.displayNameFn(groups[0])}
          showObjectUrl={props.showObjectUrl}
        />
      );
    }
  });
  return changelogGroups;
}

export function ImportChangelogSectionGroup(props: ImportChangelogProps): JSX.Element {
  // Each changelog section has a section header, with groups of logs separated by a line break.
  // Because generateSections only creates groups, we need to join these groups into sections (ie. Templates)
  const sections = generateSections(props.changelog);
  const sectionConfigMap: Map<string, ObjectSectionConfig> = new Map();
  const sectionToChangelogGroups: Map<string, Array<Array<MigrationObjectChange>>> = new Map();

  sections.forEach((section) => {
    updateChangelogGroupsForSection(section, sectionToChangelogGroups, sectionConfigMap);
  });

  const changelogGroups: Array<JSX.Element> = getChangelogComponents(
    sectionToChangelogGroups,
    sectionConfigMap,
    props
  );

  return <>{changelogGroups}</>;
}

export default function ImportChangelog(props: ImportChangelogProps): JSX.Element {
  const label = props.changelogSource === ChangelogSource.REVIEW ? 'Review & Apply Migration' : 'Changelog';
  const highImpactChangelog = getHighImpactChangelogFromChangelog(props.changelog);
  let highImpactHighlights;
  if (highImpactChangelog.length !== 0) {
    const highImpactProps = {...props, changelog: highImpactChangelog};
    highImpactHighlights = <ImportChangelogSectionGroup {...highImpactProps} />;
  } else {
    highImpactHighlights = <p>There are no changes flagged as high impact.</p>;
  }

  return (
    <div className="u-spacing-top" {...getTestProps({component: 'ImportChangelog', element: 'div'})}>
      <>
        <FieldLabel label={label} className={css(styles.topLabel)} />
        <br />
      </>
      <section
        aria-label="High Impact Change Highlights"
        {...getTestProps({component: 'ImportChangelog', element: 'highImpactHighlights'})}
      >
        <FieldLabel label="High Impact Change Highlights" className={css(styles.groupLabel)} />
        {highImpactHighlights}
      </section>
      <section
        aria-label="All Changes"
        {...getTestProps({component: 'ImportChangelog', element: 'allChanges'})}
      >
        <FieldLabel label="All Changes" className={css(styles.groupLabel)} />
        <ImportChangelogSectionGroup {...props} />
      </section>
    </div>
  );
}

const styles = StyleSheet.create({
  groupLabel: {
    fontSize: 14,
  },
  topLabel: {
    fontSize: 16,
  },
});
